// insights — the kiosk analytics suite (Self Service Orders → Insights).
// See README.md. Keys must exist in BOTH maps (checked by npm run check:locales).
export const en = {
  // --- Hub -------------------------------------------------------------
  'insights.hub.title': 'Kiosk Insights',
  'insights.hub.intro': 'How fast the counter is, when the rush arrives, where the orders come from, and what the festival calendar does to all three.',
  'insights.hub.nextFestival': 'Next festival',
  'insights.hub.nextFestivalSub': 'The staffing decision with a deadline on it.',
  'insights.hub.openCalendar': 'Open the calendar',
  'insights.backToHub': 'Insights',
  'insights.close': 'Close',

  // --- Report catalogue ------------------------------------------------
  'insights.report.festivals.title': 'Festival & holiday calendar',
  'insights.report.festivals.question': 'Which festivals actually move trade, and by how much against a normal week?',
  'insights.report.speed.title': 'Speed of service',
  'insights.report.speed.question': 'How fast are we, and which step — queue, cook or handover — is costing the time?',
  'insights.report.demand.title': 'Demand & peak hours',
  'insights.report.demand.question': 'When does volume arrive, and at what point does service start to break?',
  'insights.report.channels.title': 'Channel performance',
  'insights.report.channels.question': 'Where should we invest — more kiosks, more app promotion, more delivery capacity?',
  'insights.report.payments.title': 'Payment mix & settlement',
  'insights.report.payments.question': 'How is money arriving, and what is it costing us in counter time and risk?',
  'insights.report.accuracy.title': 'Accuracy & failures',
  'insights.report.accuracy.question': 'How often do we get it wrong, at which stage, and in which stores?',
  'insights.report.collecting': 'Collecting data',
  'insights.report.collectingHint': 'The measurements this report needs have just been switched on — it opens once there is enough history to be worth reading.',

  // --- Forecast card ---------------------------------------------------
  'insights.forecast.festival': 'Festival',
  'insights.forecast.daysRemaining': 'Days away',
  'insights.forecast.lastYear': 'Last year’s festive week',
  'insights.forecast.orders': '{n} orders',
  'insights.forecast.staffing': 'Suggested uplift',
  'insights.forecast.staffingHint': '≈{pct}% more than a normal week',
  'insights.forecast.noPrior': 'First time we are recording this festival, so there is nothing to project from yet.',

  // --- Filters ---------------------------------------------------------
  'insights.period.today': 'Today',
  'insights.period.mtd': 'MTD',
  'insights.period.qtd': 'QTD',
  'insights.period.ytd': 'YTD',
  'insights.period.custom': 'Custom',
  'insights.compare.day': 'Day to day',
  'insights.compare.period': 'Period to period',
  'insights.filter.store': 'Store',
  'insights.filter.allStores': 'All stores ({count})',
  'insights.filter.festival': 'Festival',
  'insights.filter.noFestival': 'Any period',
  'insights.filter.from': 'From',
  'insights.filter.to': 'To',
  'insights.updatedAt': 'Updated {time}',
  'insights.refresh': 'Refresh',
  'insights.export': 'Export',

  // --- Statistical honesty ---------------------------------------------
  'insights.sample.n': 'n = {n}',
  'insights.sample.weak': 'small sample',
  'insights.sample.weakHint': 'Too few orders to draw a conclusion from.',
  'insights.delta.noBase': 'Nothing traded in the comparison period, so there is no percentage to show.',
  'insights.delta.noBaseShort': 'no base',
  'insights.partialDay': 'Today is still in progress — compared against the same hours (to {hour}:00) of the comparison day, not a full one.',
  'insights.reconstructed': '{count} of {total} orders have timings rebuilt from history rather than measured live.',
  'insights.metric.netSales': 'Net sales',
  'insights.metric.uplift': 'Uplift vs a normal week',

  // --- Calendar --------------------------------------------------------
  'insights.calendar.gridTitle': '{year} trading calendar',
  'insights.calendar.gridSub': 'The festive week is filled in; the festival itself is marked on its day. Click a day to compare it with last year.',
  'insights.calendar.legendDay': 'Festival day',
  'insights.calendar.legendHoliday': 'Public holiday',
  'insights.calendar.countFestivals': 'festivals',
  'insights.calendar.countFestiveDays': 'festive days',
  'insights.calendar.countHolidays': 'public holidays',
  'insights.calendar.legendRunUp': 'Festive week (run-up)',
  'insights.calendar.legendWeekend': 'Weekend',
  'insights.calendar.sidebarTitle': '{year} festivals',
  'insights.calendar.festiveWeek': 'festive week',
  'insights.calendar.today': 'today',
  'insights.calendar.inDays': 'in {n} days',
  'insights.calendar.daysAgo': '{n} days ago',
  'insights.calendar.annualTitle': 'Daily net sales across the year',
  'insights.calendar.annualSub': 'Shaded bands are festive weeks, so seasonality is visible at a glance.',
  'insights.calendar.rankTitle': 'Festivals ranked by uplift',
  'insights.calendar.rankSub': 'Against the median of the four preceding non-festival weeks. The dashed line is a normal week.',
  'insights.calendar.yoyTitle': '{name}: this year against last',
  'insights.calendar.yoySub': 'Aligned day 1 to day 1 of the run-up — never by calendar date, because the festival moves.',
  'insights.calendar.dayN': 'Day {n}',
  'insights.calendar.festivalDay': 'Festival day',
  'insights.calendar.alignedNote': 'Aligned on the festival: {a} against {b}. Comparing these by calendar date would line up different weekdays.',
  'insights.calendar.awaitingTitle': 'Awaiting a date',
  'insights.calendar.awaitingSub': 'These move each year, so their {year} date is announced rather than calculated. Set it here.',
  'insights.calendar.setDateFor': 'Set the date for {name}',
  'insights.calendar.addEvent': 'Add event',
  'insights.calendar.seedAction': 'Load the Mauritian calendar',
  'insights.calendar.seedDoneTitle': 'Calendar loaded',
  'insights.calendar.seedDoneBody': '{festivals} festival(s) and {dates} date(s) added, covering {from} to {to}. Moving festivals beyond that are announced each year — add those dates here as they are gazetted.',
  'insights.calendar.seedFailTitle': 'Could not load the calendar',
  'insights.calendar.fieldName': 'Name',
  'insights.calendar.namePlaceholder': 'Store anniversary, local fair, promotion…',
  'insights.calendar.fieldCategory': 'Category',
  'insights.calendar.fieldIcon': 'Icon',
  'insights.calendar.fieldDate': 'Date',
  'insights.calendar.fieldWindow': 'Run-up days',
  'insights.calendar.windowHint': 'The festive week is the run-up ENDING on the event date — customers eat out before the day, not after it.',
  'insights.calendar.fieldPublicHoliday': 'This is a gazetted public holiday',
  'insights.calendar.save': 'Save event',
  'insights.calendar.saving': 'Saving…',
  'insights.calendar.removeDate': 'Remove date',
  'insights.calendar.removeDateTitle': 'Remove {name} from this year?',
  'insights.calendar.removeDateBody': 'The occurrence on {date} is removed, along with its festive-week figures. The festival itself is kept.',
  'insights.calendar.removeFestival': 'Delete festival',
  'insights.calendar.removeFestivalTitle': 'Delete {name} entirely?',
  'insights.calendar.removeFestivalBody': 'Every year’s occurrence goes with it, and year-over-year comparisons for this festival will no longer be possible.',

  // --- Empty states ----------------------------------------------------
  'insights.empty.noTradeTitle': 'No trade recorded this year',
  'insights.empty.noTradeBody': 'A real zero, not a loading error — no orders were placed in this period for the selected store.',
  'insights.empty.noUpliftTitle': 'Not enough history to measure uplift',
  'insights.empty.noUpliftBody': 'Uplift compares a festive week with the four non-festival weeks before it. Those weeks need trade in them.',
  'insights.empty.noPriorTitle': 'No earlier occurrence recorded',
  'insights.empty.noPriorBody': 'This is the first year of this festival on the calendar, so there is nothing to compare it against yet.',
  'insights.empty.noFestivalsTitle': 'No festivals dated for this year',
  'insights.empty.noFestivalsBody': 'Add an event, or set a date for one of the moving festivals listed below.',
  'insights.empty.noCalendarTitle': 'This calendar has not been set up yet',
  'insights.empty.noCalendarBody': 'No festivals or public holidays have been added for this restaurant. Load the published Mauritian calendar to fill it in — you can edit anything afterwards, and your own events are never touched.',

  // --- Categories ------------------------------------------------------
  'insights.category.PUBLIC': 'Public holiday',
  'insights.category.RELIGIOUS': 'Religious festival',
  'insights.category.CULTURAL': 'Cultural event',
  'insights.category.SCHOOL': 'School holidays',
  'insights.category.PROMOTION': 'Promotion',
  'insights.category.STORE': 'Store event',
  'insights.category.OTHER': 'Other',

  // --- Errors ----------------------------------------------------------
  'insights.error.load': 'Could not load the calendar.',
  'insights.error.saveTitle': 'Could not save',
};

export const fr = {
  // --- Hub -------------------------------------------------------------
  'insights.hub.title': 'Analyses kiosque',
  'insights.hub.intro': 'La rapidité du comptoir, l’heure de pointe, l’origine des commandes, et l’effet du calendrier des fêtes sur les trois.',
  'insights.hub.nextFestival': 'Prochaine fête',
  'insights.hub.nextFestivalSub': 'La décision d’effectifs qui a une échéance.',
  'insights.hub.openCalendar': 'Ouvrir le calendrier',
  'insights.backToHub': 'Analyses',
  'insights.close': 'Fermer',

  // --- Report catalogue ------------------------------------------------
  'insights.report.festivals.title': 'Calendrier des fêtes et jours fériés',
  'insights.report.festivals.question': 'Quelles fêtes font vraiment bouger le chiffre, et de combien par rapport à une semaine normale ?',
  'insights.report.speed.title': 'Rapidité du service',
  'insights.report.speed.question': 'Sommes-nous rapides, et quelle étape — attente, cuisson ou remise — coûte le temps ?',
  'insights.report.demand.title': 'Demande et heures de pointe',
  'insights.report.demand.question': 'Quand le volume arrive-t-il, et à partir de quel moment le service se dégrade-t-il ?',
  'insights.report.channels.title': 'Performance par canal',
  'insights.report.channels.question': 'Où investir — plus de bornes, plus de promotion de l’application, plus de livraison ?',
  'insights.report.payments.title': 'Moyens de paiement et encaissement',
  'insights.report.payments.question': 'Comment l’argent arrive-t-il, et que cela nous coûte-t-il en temps de comptoir et en risque ?',
  'insights.report.accuracy.title': 'Erreurs et incidents',
  'insights.report.accuracy.question': 'À quelle fréquence nous trompons-nous, à quelle étape, et dans quels points de vente ?',
  'insights.report.collecting': 'Collecte en cours',
  'insights.report.collectingHint': 'Les mesures nécessaires à ce rapport viennent d’être activées — il s’ouvrira dès qu’il y aura assez d’historique.',

  // --- Forecast card ---------------------------------------------------
  'insights.forecast.festival': 'Fête',
  'insights.forecast.daysRemaining': 'Jours restants',
  'insights.forecast.lastYear': 'Semaine festive l’an dernier',
  'insights.forecast.orders': '{n} commandes',
  'insights.forecast.staffing': 'Renfort suggéré',
  'insights.forecast.staffingHint': '≈{pct} % de plus qu’une semaine normale',
  'insights.forecast.noPrior': 'C’est la première fois que nous enregistrons cette fête : aucune projection possible pour l’instant.',

  // --- Filters ---------------------------------------------------------
  'insights.period.today': 'Aujourd’hui',
  'insights.period.mtd': 'Mois',
  'insights.period.qtd': 'Trimestre',
  'insights.period.ytd': 'Année',
  'insights.period.custom': 'Personnalisé',
  'insights.compare.day': 'Jour à jour',
  'insights.compare.period': 'Période à période',
  'insights.filter.store': 'Point de vente',
  'insights.filter.allStores': 'Tous les points de vente ({count})',
  'insights.filter.festival': 'Fête',
  'insights.filter.noFestival': 'Toute période',
  'insights.filter.from': 'Du',
  'insights.filter.to': 'Au',
  'insights.updatedAt': 'Mis à jour à {time}',
  'insights.refresh': 'Actualiser',
  'insights.export': 'Exporter',

  // --- Statistical honesty ---------------------------------------------
  'insights.sample.n': 'n = {n}',
  'insights.sample.weak': 'échantillon faible',
  'insights.sample.weakHint': 'Trop peu de commandes pour en tirer une conclusion.',
  'insights.delta.noBase': 'Aucune activité sur la période de comparaison : aucun pourcentage ne peut être affiché.',
  'insights.delta.noBaseShort': 'sans base',
  'insights.partialDay': 'La journée est en cours — comparée aux mêmes heures (jusqu’à {hour}h00) du jour de référence, et non à une journée complète.',
  'insights.reconstructed': '{count} commandes sur {total} ont des durées reconstituées à partir de l’historique, non mesurées en direct.',
  'insights.metric.netSales': 'Ventes nettes',
  'insights.metric.uplift': 'Hausse vs une semaine normale',

  // --- Calendar --------------------------------------------------------
  'insights.calendar.gridTitle': 'Calendrier commercial {year}',
  'insights.calendar.gridSub': 'La semaine festive est remplie ; la fête elle-même est marquée sur son jour. Cliquez un jour pour le comparer à l’an dernier.',
  'insights.calendar.legendDay': 'Jour de la fête',
  'insights.calendar.legendHoliday': 'Jour férié',
  'insights.calendar.countFestivals': 'fêtes',
  'insights.calendar.countFestiveDays': 'jours festifs',
  'insights.calendar.countHolidays': 'jours fériés',
  'insights.calendar.legendRunUp': 'Semaine festive (avant la fête)',
  'insights.calendar.legendWeekend': 'Week-end',
  'insights.calendar.sidebarTitle': 'Fêtes {year}',
  'insights.calendar.festiveWeek': 'semaine festive',
  'insights.calendar.today': 'aujourd’hui',
  'insights.calendar.inDays': 'dans {n} jours',
  'insights.calendar.daysAgo': 'il y a {n} jours',
  'insights.calendar.annualTitle': 'Ventes nettes quotidiennes sur l’année',
  'insights.calendar.annualSub': 'Les bandes ombrées sont les semaines festives : la saisonnalité se lit d’un coup d’œil.',
  'insights.calendar.rankTitle': 'Fêtes classées par hausse',
  'insights.calendar.rankSub': 'Par rapport à la médiane des quatre semaines sans fête précédentes. La ligne pointillée représente une semaine normale.',
  'insights.calendar.yoyTitle': '{name} : cette année contre l’an dernier',
  'insights.calendar.yoySub': 'Aligné jour 1 sur jour 1 du compte à rebours — jamais par date, car la fête se déplace.',
  'insights.calendar.dayN': 'Jour {n}',
  'insights.calendar.festivalDay': 'Jour de la fête',
  'insights.calendar.alignedNote': 'Aligné sur la fête : {a} contre {b}. Une comparaison par date ferait coïncider des jours de semaine différents.',
  'insights.calendar.awaitingTitle': 'En attente d’une date',
  'insights.calendar.awaitingSub': 'Ces fêtes se déplacent chaque année : leur date {year} est annoncée, pas calculée. Saisissez-la ici.',
  'insights.calendar.setDateFor': 'Définir la date de {name}',
  'insights.calendar.addEvent': 'Ajouter un événement',
  'insights.calendar.seedAction': 'Charger le calendrier mauricien',
  'insights.calendar.seedDoneTitle': 'Calendrier chargé',
  'insights.calendar.seedDoneBody': '{festivals} fête(s) et {dates} date(s) ajoutées, de {from} à {to}. Les fêtes mobiles au-delà sont annoncées chaque année — ajoutez ces dates ici dès leur publication.',
  'insights.calendar.seedFailTitle': 'Impossible de charger le calendrier',
  'insights.calendar.fieldName': 'Nom',
  'insights.calendar.namePlaceholder': 'Anniversaire du point de vente, foire locale, promotion…',
  'insights.calendar.fieldCategory': 'Catégorie',
  'insights.calendar.fieldIcon': 'Icône',
  'insights.calendar.fieldDate': 'Date',
  'insights.calendar.fieldWindow': 'Jours avant',
  'insights.calendar.windowHint': 'La semaine festive est la période qui SE TERMINE le jour de l’événement — les clients sortent avant le jour même, pas après.',
  'insights.calendar.fieldPublicHoliday': 'Il s’agit d’un jour férié officiel',
  'insights.calendar.save': 'Enregistrer',
  'insights.calendar.saving': 'Enregistrement…',
  'insights.calendar.removeDate': 'Retirer la date',
  'insights.calendar.removeDateTitle': 'Retirer {name} de cette année ?',
  'insights.calendar.removeDateBody': 'L’occurrence du {date} est retirée, ainsi que ses chiffres de semaine festive. La fête elle-même est conservée.',
  'insights.calendar.removeFestival': 'Supprimer la fête',
  'insights.calendar.removeFestivalTitle': 'Supprimer {name} entièrement ?',
  'insights.calendar.removeFestivalBody': 'Toutes les occurrences seront supprimées et les comparaisons d’une année sur l’autre ne seront plus possibles pour cette fête.',

  // --- Empty states ----------------------------------------------------
  'insights.empty.noTradeTitle': 'Aucune activité enregistrée cette année',
  'insights.empty.noTradeBody': 'Un vrai zéro, pas une erreur de chargement — aucune commande sur cette période pour le point de vente choisi.',
  'insights.empty.noUpliftTitle': 'Historique insuffisant pour mesurer la hausse',
  'insights.empty.noUpliftBody': 'La hausse compare une semaine festive aux quatre semaines sans fête qui la précèdent. Ces semaines doivent contenir de l’activité.',
  'insights.empty.noPriorTitle': 'Aucune occurrence antérieure enregistrée',
  'insights.empty.noPriorBody': 'C’est la première année de cette fête au calendrier : il n’y a encore rien à comparer.',
  'insights.empty.noFestivalsTitle': 'Aucune fête datée pour cette année',
  'insights.empty.noFestivalsBody': 'Ajoutez un événement, ou donnez une date à l’une des fêtes mobiles ci-dessous.',
  'insights.empty.noCalendarTitle': 'Ce calendrier n’est pas encore configuré',
  'insights.empty.noCalendarBody': 'Aucune fête ni aucun jour férié n’a été ajouté pour ce restaurant. Chargez le calendrier mauricien officiel pour le remplir — vous pourrez tout modifier ensuite, et vos propres événements ne sont jamais touchés.',

  // --- Categories ------------------------------------------------------
  'insights.category.PUBLIC': 'Jour férié',
  'insights.category.RELIGIOUS': 'Fête religieuse',
  'insights.category.CULTURAL': 'Événement culturel',
  'insights.category.SCHOOL': 'Vacances scolaires',
  'insights.category.PROMOTION': 'Promotion',
  'insights.category.STORE': 'Événement du point de vente',
  'insights.category.OTHER': 'Autre',

  // --- Errors ----------------------------------------------------------
  'insights.error.load': 'Impossible de charger le calendrier.',
  'insights.error.saveTitle': 'Enregistrement impossible',
};
